import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  correlationId: string;
}

function generateCorrelationId(): string {
  return `plp-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, correlationId: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, correlationId: generateCorrelationId() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[PLP ErrorBoundary]', error, info);
    // Send to monitoring endpoint
    fetch('/api/error-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        correlationId: this.state.correlationId,
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, correlationId: '' });
    window.location.reload();
  };

  handleCopyId = () => {
    navigator.clipboard.writeText(this.state.correlationId).then(() => {
      toast.success('Error ID copied to clipboard');
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
          <div className="mb-4 rounded-full p-3" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <AlertTriangle size={32} style={{ color: '#ef4444' }} />
          </div>
          <h2 className="font-body mb-2 text-lg font-semibold" style={{ color: '#F7F8F4' }}>
            Something went wrong
          </h2>
          <p className="font-body mb-4 max-w-md text-sm" style={{ color: '#A8B8AE' }}>
            We encountered an unexpected error. Our team has been notified.
            Please try refreshing the page.
          </p>
          <div className="mb-6 flex items-center gap-2 rounded-lg px-3 py-2 font-mono text-xs" style={{ background: 'rgba(255,255,255,0.03)', color: '#5A5650' }}>
            ID: {this.state.correlationId}
            <button onClick={this.handleCopyId} className="ml-2 hover:opacity-70" title="Copy error ID">
              <Copy size={12} />
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 rounded-lg px-4 py-2 font-body text-sm font-medium transition-all hover:opacity-90"
              style={{ background: '#7CFF6B', color: '#07110D' }}
            >
              <RefreshCw size={14} /> Retry
            </button>
            <a
              href="/"
              className="flex items-center gap-2 rounded-lg px-4 py-2 font-body text-sm transition-all hover:opacity-80"
              style={{ border: '1px solid rgba(255,255,255,0.1)', color: '#A8B8AE' }}
            >
              <Home size={14} /> Go Home
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function withErrorBoundary<P extends object>(Component: React.ComponentType<P>) {
  return function WrappedComponent(props: P) {
    return (
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
}
